import { oc } from "@orpc/contract";
import { z } from "zod";
import { SystemEntitySchema } from "@erp/workflows";
import { CHARGEABLE_ENTITIES, TroubleDataSchema } from "@erp/shared-types";

const UUIDSchema = z.uuid();
const DecimalStringSchema = z.string().regex(/^-?\d+(\.\d+)?$/, {
  error: "Must be a numeric string",
});

/**
 * Charge direction enum
 */
export const ChargeDirectionSchema = z.enum(["DEBIT", "CREDIT"]);

/**
 * Charge category preview (from CoreEnumOption)
 */
const ChargeCategoryPreviewSchema = z.object({
  id: UUIDSchema,
  value: z.string(),
  label: z.string().optional(),
});

/**
 * Allocation group preview
 */
const AllocationGroupPreviewSchema = z.object({
  id: UUIDSchema,
  allocationMethod: z.string(),
  status: z.string(),
});

/**
 * Full charge DTO with all fields
 */
export const ChargeSchema = z.object({
  id: UUIDSchema,
  chargeCategoryId: UUIDSchema,
  direction: ChargeDirectionSchema,
  isEstimate: z.boolean(),
  amount: DecimalStringSchema,
  currencyCode: z.string(),
  docType: z.string(),
  docId: UUIDSchema,
  docLineId: UUIDSchema.nullable(), // UUID for line charges, null for header charges
  allocatedToGroupId: UUIDSchema.nullable().optional(),
  allocatedFromGroupId: UUIDSchema.nullable().optional(),
  reversesChargeId: UUIDSchema.nullable().optional(), // If this charge is a reversal, points to the original charge
  sourceReference: z.string().nullable().optional(),
  ediMetadata: z.record(z.string(), z.unknown()).nullable().optional(),
  originMetadata: z.record(z.string(), z.unknown()).nullable().optional(),
  troubleData: TroubleDataSchema,
  isVisibleOnExternalDocuments: z.boolean(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  deletedAt: z.coerce.date().nullable().optional(),
  // Related entities (optional includes)
  chargeCategory: ChargeCategoryPreviewSchema.optional(),
  allocatedToGroup: AllocationGroupPreviewSchema.nullable().optional(),
  allocatedFromGroup: AllocationGroupPreviewSchema.nullable().optional(),
});

/**
 * Input schema for getById
 */
export const GetChargeByIdInputSchema = z.object({
  id: UUIDSchema,
});

/**
 * Response schema for getById
 */
export const GetChargeByIdResponseSchema = z.object({
  charge: ChargeSchema.nullable(),
});

/**
 * Input schema for getChargesForEntity
 */
export const GetChargesForEntityInputSchema = z.object({
  entity: SystemEntitySchema.extend({
    entityType: z.enum(CHARGEABLE_ENTITIES),
  }),
  headerDocId: UUIDSchema.nullable().optional(),
  includeLineCharges: z.boolean().optional().default(true),
  /**
   * Also return charges that don't count toward totals: Famous reversal
   * ('R') and superseded ('Y') rows, and informational PO charges. Off by
   * default, so callers get only active charges.
   */
  includeInactive: z.boolean().optional().default(false),
});

/**
 * Response schema for getChargesForEntity
 */
export const GetChargesForEntityResponseSchema = z.object({
  charges: z.array(ChargeSchema),
});

/**
 * Charges contract definitions
 */
export const chargesContracts = {
  getById: oc
    .input(GetChargeByIdInputSchema)
    .output(GetChargeByIdResponseSchema)
    .route({
      method: "GET",
      summary: "Get a charge by ID",
      description:
        "Returns one charge (freight, fee, tax, or other cost) with its category, debit/credit direction, amount, source document, and allocation group links, or null if not found.",
    }),
  getForEntity: oc
    .input(GetChargesForEntityInputSchema)
    .output(GetChargesForEntityResponseSchema)
    .route({
      method: "GET",
      summary: "List charges on a document",
      description:
        "Lists charges attached to an invoice, order, or document line. Pass the header document ID for line entities; header charges can include line-level charges too.",
    }),
} as const;

// Export types
export type Charge = z.infer<typeof ChargeSchema>;
export type ChargeDirection = z.infer<typeof ChargeDirectionSchema>;
export type GetChargeByIdInput = z.infer<typeof GetChargeByIdInputSchema>;
export type GetChargeByIdResponse = z.infer<typeof GetChargeByIdResponseSchema>;
export type GetChargesForEntityInput = z.infer<
  typeof GetChargesForEntityInputSchema
>;
export type GetChargesForEntityResponse = z.infer<
  typeof GetChargesForEntityResponseSchema
>;
